import axios, { type AxiosInstance, type AxiosRequestConfig } from 'axios';
import { getAccessToken, handleUnauthorized } from '@/auth/tokenStore';
import { getHub } from '@/auth/hubStore';
import { authDisabled } from '@/auth/oidcConfig';
import type { ApiEnvelope } from './types';
import { ulid } from './ulid';

/**
 * The caller's own hub slug for a `{hub}`-scoped tenant/oversight/metering path. In real auth this
 * is the slug the user signed in with (HubLogin → setHub) or the slug derived from the explicit
 * issuer's realm (`opsflow-<slug>`, AuthProvider) — the server rejects any mismatch 403 (isolation
 * is JWT-derived, the path segment is RESTful identity only). Under REAL auth an unset hub is a
 * CONFIGURATION ERROR and throws — the SPA must never silently call a placeholder tenant's paths.
 * Only the dev-standalone mock (VITE_AUTH_DISABLED) uses the 'acme' placeholder; those endpoints
 * degrade to teaching empty states there anyway (no Track A backend / no bearer).
 */
export function tenantHub(): string {
  const hub = getHub();
  if (hub) return hub;
  if (authDisabled()) return 'acme';
  throw new Error(
    'Hub is not configured: real auth needs a hub slug — sign in via the hub login, or point ' +
      'VITE_OIDC_ISSUER at a realm-per-hub issuer (…/realms/opsflow-<slug>).',
  );
}

/**
 * The product-API client. Targets the `/api` path — dev-proxied by Vite now
 * (VITE_DEV_API_PROXY_TARGET), served by nginx in production (DEPLOY.md). The Track A product API
 * is not built yet; this client is ready for PS-2 to consume it.
 *
 * Every request carries the OIDC bearer; a 401 rejects closed (forces re-auth) — the SPA never
 * silently degrades to an unauthenticated state.
 */
export const apiClient: AxiosInstance = axios.create({
  baseURL: '/api',
  timeout: 20_000,
  headers: { Accept: 'application/json' },
});

apiClient.interceptors.request.use((config) => {
  const token = getAccessToken();
  if (token) {
    config.headers.set('Authorization', `Bearer ${token}`);
  }
  return config;
});

apiClient.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error?.response?.status === 401) {
      handleUnauthorized(); // reject-closed
    }
    return Promise.reject(error);
  },
);

/** Unwrap the `{status, traceId, data}` success envelope every product-API 2xx carries. */
export function unwrap<T>(env: ApiEnvelope<T>): T {
  return env.data;
}

/**
 * A fresh `ol-idempotency-key` header for a mutating POST. ULID (first char 0–7 — HANDOVER §7).
 * Pass an existing key to retry the SAME logical operation without a duplicate side effect.
 */
export function idempotentConfig(key: string = ulid(), extra?: AxiosRequestConfig): AxiosRequestConfig {
  return { ...extra, headers: { ...extra?.headers, 'ol-idempotency-key': key } };
}
